import { ANDROID_URL } from "../../components/appBanner";

export type FaqSection = { category: string; questions: { q: string; a: string }[] };

/* The answers are trusted, hand-written HTML (a few hold links). */
export const FAQS: FaqSection[] = [
  {
    category: "About !tldr",
    questions: [
      {
        q: "What is !tldr?",
        a: "!tldr gives you something worth reading instead of mindless scrolling. We deliver mobile-optimized articles that break down complex topics into short, readable articles—designed for coffee breaks and commutes, not hour-long reading sessions.",
      },
      {
        q: "How is !tldr different from other content platforms?",
        a: "Unlike news sites or social media, we focus on enduring insights rather than breaking news. We're not trying to make you scroll more—we're making the time you already spend scrolling actually worthwhile. No posting, no performing, no anxiety-inducing updates.",
      },
      {
        q: "What does '!tldr' stand for?",
        a: "not too long; did read <br/><br/> It's a playful twist on <a href='https://en.wikipedia.org/wiki/TL;DR' target='_blank' class='text-blue-500'>TL;DR</a> (too long; didn't read). In many programming languages, '!' is used as the 'not' operator. So '!tldr' means 'not tl;dr' or 'not too long; did read'.",
      },
    ],
  },
  {
    category: "Content & Quality",
    questions: [
      {
        q: "Is the content AI-generated?",
        a: "Yes, all articles are generated using Anthropic's Claude AI. We use advanced AI to create articles on diverse topics, written from various expert personas. While we're transparent about AI generation, we encourage readers to verify critical information independently.",
      },
      {
        q: "Is the content reviewed by humans before publication?",
        a: "No. Articles are published directly without human review. We use advanced AI systems and quality controls in our generation process, but readers should verify important information from primary sources when making critical decisions.",
      },
      {
        q: "Are the audio versions also AI-generated?",
        a: "Yes. Each article includes an AI-generated audio version, allowing you to listen to content while commuting, exercising, or multitasking. The audio is generated using text-to-speech AI technology.",
      },
      {
        q: "What topics do you cover?",
        a: "We focus on concepts with lasting intellectual value: psychology over politics, fundamental principles over fleeting trends, human nature over hot takes. Think cognitive biases, historical patterns, scientific breakthroughs, philosophical ideas, and practical wisdom.",
      },
      {
        q: "Is there any content structure?",
        a: "Every article follows a consistent format: a 5-bullet summary for quick scanning, an engaging introduction, 3 key points, each with a takeaway, and a a short conclusion. Designed for reading on your phone.",
      },
      {
        q: "Who are the different 'authors'?",
        a: "Each article features distinct personas—neuroscientists, philosophers, historians, practitioners—each with a consistent voice and unique perspective. Think of it as having a diverse panel of experts at your fingertips.",
      },
    ],
  },
  {
    category: "Using !tldr",
    questions: [
      {
        q: "Can I create an account on !tldr?",
        a: "Yes! You can create an account by signing in with Google, Apple, or email. Creating an account allows you to follow authors, build collections, bookmark articles, and get personalized recommendations.",
      },
      {
        q: "How often is new content published?",
        a: "Each author aims to publish 2-3 articles per week. This gives us enough time to generate new articles and also gives you something meaningful to read whenever curiosity strikes. Our focus is on consistent quality over quantity.",
      },
      {
        q: "How can I bookmark or save articles for later?",
        a: "Tap 'Add to Read Later' or 'Add to Collection' item on any article menu. You'll need to sign in to save bookmarks, but once you do, you can organize articles into collections and access them from any device.",
      },
      {
        q: "Do you have a mobile app?",
        a: `Yes! !tldr is available on the <a href='https://apps.apple.com/au/app/tldr/id6757189366' target='_blank' class='text-blue-500'>App Store</a> and <a href='${ANDROID_URL}' target='_blank' class='text-blue-500'>Google Play</a>. You can also use the mobile-optimized website.`,
      },
      {
        q: "Can I share articles?",
        a: "You can share article links with friends, but we don't have in-app social features. We're not trying to be social media.",
      },
      {
        q: "Can I interact with other readers?",
        a: "No, we deliberately don't have this feature to prevent the platform from becoming a social media platform. !tldr is for reading, not social interaction, not social interaction. If you want to discuss an article with others, feel free to share it on other social platforms such as Facebook, LinkedIn, Reddit etc.",
      },
      {
        q: "How do I delete my account?",
        a: "You can delete your account at any time from your account settings. After requesting deletion, you'll receive an email to confirm. Once confirmed, your account and all associated data will be permanently deleted within 90 days.",
      },
    ],
  },
  {
    category: "Philosophy & Approach",
    questions: [
      {
        q: "Why focus on 'enduring' content?",
        a: "When someone reads our articles years from now, we want it to still be worth reading—even if specific details have evolved. This means timeless psychology over trending politics, fundamental principles over temporary frameworks.",
      },
      {
        q: "What does success look like for !tldr?",
        a: "For us, success means users feeling good about the time they spent, not hollow.",
      },
      {
        q: "Who is !tldr for?",
        a: "!tldr is for anyone who scrolls and wants that time to be more meaningful. If you're curious about the world but don't have hours for deep dives, if you want something better than news feeds and social media—!tldr is for you.",
      },
    ],
  },
];
